import React, { memo } from 'react';
import { Flex } from 'antd';
import { BottomSheet } from 'react-spring-bottom-sheet';
import ExternalMapAlertsForm from '../partials/ExternalMapAlertsForm';
import CustomShipmentText from '@/components/CustomShipmentText';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import PageContent from './PageContent';
import ShareHeaderActions from './ShareHeaderActions';
import DisplayTimeDropdown from './DisplayTimeDropdown';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { MobileBottomSheetProps } from './types';
import MapPointsCards from './MapPointsCards';
import PizzaTracker from './PizzaTracker/PizzaTracker';
import {
  AtRiskTringleIcon,
  ShareAlertIcon,
  getShipmentLegModeIcon,
} from '@/assets/icons/icons';
import { getLegModeIconType } from '../ControlTower/components/shipment/legModeUtils';
import ReportIssueCard from '../partials/ReportIssue/ReportIssueCard';
import { ExecutionLegDetail } from '@/apis/types';

const MobileBottomSheet: React.FC<MobileBottomSheetProps> = memo((props) => {
  const {
    open = true,
    handleClose,
    isSharePage,
    showAlertForm,
    setShowAlertForm,

    cardVisibilityState,
    handleCardActions,
    canShowReportIssueCTA,

    showAlerts,
    alertsData,
    alertIndex,
    setAlertIndex,
    uuid,
    shipment,
    shipmentId,
    legType,
    productDetails,
    pointState,
    capitalize,
    getFormattedTime,
    downloadFile,
    halfHeight,
    setHalfHeight,
    showFull,
    setShowFull,
    arrowLink,
    CommentIcon,
    AtRisk,
    FileSvgIcon,
    openDropdown,
    setOpenDropdown,
    displayTime,
    onChangeDisplayTime,
    fetchSingleShipmentData,
    showPointCard,
    setPointState,
    setShowPointCard,
    currentPointSelected,
    setCurrentPointSelected,
    selectedPointType,
    setSelectedPointType,
    selectedEventId,
    setSelectedEventId,
    onRecenter,
    refetchSharePageData,
  } = props;

  const legMode =
    (legType ?? shipment?.leg_type) === 'EL'
      ? (shipment as unknown as { mode?: ExecutionLegDetail['mode'] })?.mode
      : undefined;
  const isExecutionLeg = legType === 'EL' || shipment?.leg_type === 'EL';

  return (
    <BottomSheet
      open={open}
      expandOnContentDrag
      blocking={false}
      snapPoints={({ maxHeight }) => [
        0.15 * maxHeight,
        0.6 * maxHeight,
        maxHeight,
      ]}
      defaultSnap={({ maxHeight }) => 0.15 * maxHeight}
    >
      <div />
      <div style={{ padding: '1rem 1rem 1.5rem' }}>
        {showAlertForm ? (
          <div>
            <ExternalMapAlertsForm
              setShowAlertForm={handleClose}
              externalToken={uuid}
            />
          </div>
        ) : cardVisibilityState?.showCard ? (
          <ReportIssueCard
            cardVisibilityState={cardVisibilityState}
            handleCardActions={handleCardActions}
            shipment={shipment}
            token={uuid}
            refetchSharePageData={refetchSharePageData}
          />
        ) : showPointCard ? (
          <MapPointsCards
            pointState={pointState}
            setPointState={setPointState}
            setShowPointCard={setShowPointCard}
            shipment={shipment}
            currentPointSelected={currentPointSelected}
            setCurrentPointSelected={setCurrentPointSelected}
            selectedPointType={selectedPointType}
            setSelectedPointType={setSelectedPointType}
            selectedEventId={selectedEventId}
            setSelectedEventId={setSelectedEventId}
            onRecenter={onRecenter}
          />
        ) : (
          <Flex vertical gap={10}>
            <Flex gap={0} vertical className='custom-card-drawer-header'>
              <Flex gap={8} align='center' justify='space-between'>
                <CustomShipmentText
                  text={
                    isExecutionLeg
                      ? 'MBL'
                      : themeT('referenceId', theme.themeType)
                  }
                  fontWeight={600}
                  color='var(--text-secondary)'
                />

                {isSharePage ? (
                  <Flex gap={8} align='center'>
                    <ShareHeaderActions
                      onClick={() => setShowAlertForm(true)}
                      text='Get updates'
                      icon={<ShareAlertIcon />}
                    />

                    {canShowReportIssueCTA && (
                      <ShareHeaderActions
                        onClick={() => handleCardActions(true, false, '')}
                        text='Report issue'
                        icon={
                          <AtRiskTringleIcon
                            color={'#000'}
                            width={12}
                            height={12}
                          />
                        }
                      />
                    )}
                  </Flex>
                ) : null}
              </Flex>

              <Flex gap={8} align='center'>
                {isExecutionLeg ? (
                  <span
                    className='shipment-grid-reference-leg-icon'
                    style={{ flexShrink: 0, lineHeight: 0 }}
                    aria-hidden='true'
                  >
                    {getShipmentLegModeIcon(
                      getLegModeIconType(legMode?.name ?? legMode?.value ?? '')
                    )}
                  </span>
                ) : null}
                <TruncateWithTooltip
                  text={
                    isExecutionLeg
                      ? (shipment?.mbl ?? '')
                      : (shipment?.reference_number ?? '')
                  }
                  max={13}
                  render={(t) => (
                    <span style={{ width: 'fit-content' }}>
                      <CustomShipmentText
                        text={t}
                        fontSize='18px'
                        fontWeight={700}
                        lineHeight='24px'
                        marginTop='0px'
                      />
                    </span>
                  )}
                />
              </Flex>
              {isExecutionLeg && (
                <CustomShipmentText
                  text='Execution leg'
                  color='rgb(118, 118, 118)'
                  fontWeight={600}
                  marginTop='0px'
                />
              )}

              {isSharePage && (
                <PizzaTracker
                  shipment={shipment}
                  selectedTimezone={displayTime?.value}
                />
              )}

              {isSharePage && (
                <DisplayTimeDropdown
                  value={displayTime}
                  onChange={onChangeDisplayTime}
                  open={openDropdown}
                  setOpen={setOpenDropdown}
                />
              )}
            </Flex>

            <PageContent
              isSharePage={isSharePage}
              variant='mobile'
              showAlerts={showAlerts}
              alertsData={alertsData}
              alertIndex={alertIndex}
              setAlertIndex={setAlertIndex}
              shipment={shipment}
              shipmentId={shipmentId}
              productDetails={productDetails}
              pointState={pointState}
              capitalize={capitalize}
              getFormattedTime={getFormattedTime}
              downloadFile={downloadFile}
              halfHeight={halfHeight}
              setHalfHeight={setHalfHeight}
              showFull={showFull}
              setShowFull={setShowFull}
              arrowLink={arrowLink}
              CommentIcon={CommentIcon}
              AtRisk={AtRisk}
              FileSvgIcon={FileSvgIcon}
              fetchSingleShipmentData={fetchSingleShipmentData}
              uuid={uuid}
            />
          </Flex>
        )}
      </div>
    </BottomSheet>
  );
});

MobileBottomSheet.displayName = 'MobileBottomSheet';
export default MobileBottomSheet;
